import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Goals } from "../models/goals";
import { User } from '../models/user';
import { AuthService } from '../services/authService';
import { MealViewModel } from '../models/mealResponseModel';

@Component({
    selector: 'app-total-caloric-and-macro-intake',
    templateUrl: './total-caloric-and-macro-intake.component.html',
    styleUrl: './total-caloric-and-macro-intake.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TotalCaloricAndMacroIntakeComponent implements OnChanges {
  @Input() goals = new Goals(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
  @Input() mealViewModels: MealViewModel[] = [];
  breakfastMealViewModels: MealViewModel[] = [];
  lunchMealViewModels: MealViewModel[] = [];
  dinnerMealViewModels: MealViewModel[] = [];
  snacksMealViewModels: MealViewModel[] = [];
  user = new User('', false);
  consumedAlcoholQty = 0;
  consumedAlcoholCalories = 0;
  consumedCarbQty = 0;
  consumedCarbCalories = 0;
  consumedProteinQty = 0;
  consumedProteinCalories = 0;
  consumedFatQty = 0;
  consumedFatCalories = 0;
  totalConsumedCalories = 0;

  percentOfAlcoholGoal = 0;
  percentOfCarbGoal = 0;
  percentOfProteinGoal = 0;
  percentOfFatGoal = 0;
  percentOfCaloriesGoal = 0;

  consumedBreakfastAlcoholQty = 0;
  consumedBreakfastCarbQty = 0;
  consumedBreakfastProteinQty = 0;
  consumedBreakfastFatQty = 0;

  consumedLunchAlcoholQty = 0;
  consumedLunchCarbQty = 0;
  consumedLunchProteinQty = 0;
  consumedLunchFatQty = 0;

  consumedDinnerAlcoholQty = 0;
  consumedDinnerCarbQty = 0;
  consumedDinnerProteinQty = 0;
  consumedDinnerFatQty = 0;

  consumedSnacksAlcoholQty = 0;
  consumedSnacksCarbQty = 0;
  consumedSnacksProteinQty = 0;
  consumedSnacksFatQty = 0;

  consumedBreakfastAlcoholCalories = 0;
  consumedBreakfastCarbCalories = 0;
  consumedBreakfastProteinCalories = 0;
  consumedBreakfastFatCalories = 0;

  consumedLunchAlcoholCalories = 0;
  consumedLunchCarbCalories = 0;
  consumedLunchProteinCalories = 0;
  consumedLunchFatCalories = 0;

  consumedDinnerAlcoholCalories = 0;
  consumedDinnerCarbCalories = 0;
  consumedDinnerProteinCalories = 0;
  consumedDinnerFatCalories = 0;

  consumedSnacksAlcoholCalories = 0;
  consumedSnacksCarbCalories = 0;
  consumedSnacksProteinCalories = 0;
  consumedSnacksFatCalories = 0;

  isLoggedIn = false;

  constructor(private authService: AuthService, private cdr: ChangeDetectorRef) { }

  async ngOnInit() {
    this.isLoggedIn = await this.checkIfUserIsLoggedIn();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (this.isLoggedIn) {
      if (changes['goals']) {
        this.cdr.markForCheck(); // Manually mark for check
      }
      if (changes['goals'] || changes['mealViewModels']) {
        this.calculateCaloricAndMacroIntake();
      }
    }
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  private checkIfUserIsLoggedIn(): Promise<boolean> {
    return new Promise((resolve) => {
      this.authService.isLoggedIn().subscribe((response: any) => {
        this.user.loggedin = response.isAuthenticated;
        this.user.username = response.userName ?? "";
        resolve(this.user.loggedin);
      });
    });
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  calculateCaloricAndMacroIntake() {
    // Reset consumed quantities and calories
    this.consumedAlcoholQty = 0;
    this.consumedCarbQty = 0;
    this.consumedProteinQty = 0;
    this.consumedFatQty = 0;
    this.consumedAlcoholCalories = 0;
    this.consumedCarbCalories = 0;
    this.consumedProteinCalories = 0;
    this.consumedFatCalories = 0;
    this.totalConsumedCalories = 0;

    let mealType = "";
    if (this.mealViewModels.length > 0) {
      switch (this.mealViewModels[0].mealTypeId) {
        case 1:
          this.breakfastMealViewModels = this.mealViewModels;
          mealType = "breakfast";
          break;
        case 2:
          this.lunchMealViewModels = this.mealViewModels;
          mealType = "lunch";
          break;
        case 3:
          this.dinnerMealViewModels = this.mealViewModels;
          mealType = "dinner";
          break;
        case 4:
          this.snacksMealViewModels = this.mealViewModels;
          mealType = "snacks";
          break;
      }
    }

    // Calculate consumed quantities and calories
    this.calculateConsumedQuantitiesAndCalories(this.mealViewModels, mealType);

    // round macros to the nearest hundredth
    this.roundMacrosToTheNearestHundredth(mealType);

    // calculate calories
    this.calculateCalories(mealType);

    this.calculatePercentageOfGoalsConsumed();
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  private calculatePercentageOfGoalsConsumed() {
    // Reset percentage goals
    this.percentOfAlcoholGoal = 0;
    this.percentOfCarbGoal = 0;
    this.percentOfProteinGoal = 0;
    this.percentOfFatGoal = 0;
    this.percentOfCaloriesGoal = 0;

    this.percentOfAlcoholGoal = this.goals.alcoholCalories === 0 ? 0 : Math.round((this.consumedAlcoholCalories / this.goals.alcoholCalories) * 100);

    this.percentOfCarbGoal = this.goals.carbCalories === 0 ? 0 : Math.round((this.consumedCarbCalories / this.goals.carbCalories) * 100);

    this.percentOfProteinGoal = this.goals.proteinCalories === 0 ? 0 : Math.round((this.consumedProteinCalories / this.goals.proteinCalories) * 100);

    this.percentOfFatGoal = this.goals.fatCalories === 0 ? 0 : Math.round((this.consumedFatCalories / this.goals.fatCalories) * 100);

    this.percentOfCaloriesGoal = this.goals.totalCalories === 0 ? 0 : Math.round((this.totalConsumedCalories / this.goals.totalCalories) * 100);
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  private calculateConsumedQuantitiesAndCalories(mealViewModels: MealViewModel[], mealType: string) {
    switch (mealType) {
      case "breakfast":
        this.consumedBreakfastAlcoholQty = 0;
        this.consumedBreakfastCarbQty = 0;
        this.consumedBreakfastProteinQty = 0;
        this.consumedBreakfastFatQty = 0;

        for (let meal of mealViewModels) {
          this.consumedBreakfastAlcoholQty += meal.alcohol;
          this.consumedBreakfastCarbQty += meal.netcarbs;
          this.consumedBreakfastProteinQty += meal.protein;
          this.consumedBreakfastFatQty += meal.fat;
        }
        break;
      case "lunch":
        this.consumedLunchAlcoholQty = 0;
        this.consumedLunchCarbQty = 0;
        this.consumedLunchProteinQty = 0;
        this.consumedLunchFatQty = 0;

        for (let meal of mealViewModels) {
          this.consumedLunchAlcoholQty += meal.alcohol;
          this.consumedLunchCarbQty += meal.netcarbs;
          this.consumedLunchProteinQty += meal.protein;
          this.consumedLunchFatQty += meal.fat;
        }
        break;
      case "dinner":
        this.consumedDinnerAlcoholQty = 0;
        this.consumedDinnerCarbQty = 0;
        this.consumedDinnerProteinQty = 0;
        this.consumedDinnerFatQty = 0;

        for (let meal of mealViewModels) {
          this.consumedDinnerAlcoholQty += meal.alcohol;
          this.consumedDinnerCarbQty += meal.netcarbs;
          this.consumedDinnerProteinQty += meal.protein;
          this.consumedDinnerFatQty += meal.fat;
        }
        break;
      case "snacks":
        this.consumedSnacksAlcoholQty = 0;
        this.consumedSnacksCarbQty = 0;
        this.consumedSnacksProteinQty = 0;
        this.consumedSnacksFatQty = 0;

        for (let meal of mealViewModels) {
          this.consumedSnacksAlcoholQty += meal.alcohol;
          this.consumedSnacksCarbQty += meal.netcarbs;
          this.consumedSnacksProteinQty += meal.protein;
          this.consumedSnacksFatQty += meal.fat;
        }
        break;
    }
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  roundMacrosToTheNearestHundredth(mealType: string) {
    const roundToHundredth = (value: number) => value ? Math.round(value * 100) / 100 : 0;

    switch (mealType) {
      case "breakfast":
        this.consumedBreakfastAlcoholQty = roundToHundredth(this.consumedBreakfastAlcoholQty);
        this.consumedBreakfastCarbQty = roundToHundredth(this.consumedBreakfastCarbQty);
        this.consumedBreakfastProteinQty = roundToHundredth(this.consumedBreakfastProteinQty);
        this.consumedBreakfastFatQty = roundToHundredth(this.consumedBreakfastFatQty);
        break;
      case "lunch":
        this.consumedLunchAlcoholQty = roundToHundredth(this.consumedLunchAlcoholQty);
        this.consumedLunchCarbQty = roundToHundredth(this.consumedLunchCarbQty);
        this.consumedLunchProteinQty = roundToHundredth(this.consumedLunchProteinQty);
        this.consumedLunchFatQty = roundToHundredth(this.consumedLunchFatQty);
        break;
      case "dinner":
        this.consumedDinnerAlcoholQty = roundToHundredth(this.consumedDinnerAlcoholQty);
        this.consumedDinnerCarbQty = roundToHundredth(this.consumedDinnerCarbQty);
        this.consumedDinnerProteinQty = roundToHundredth(this.consumedDinnerProteinQty);
        this.consumedDinnerFatQty = roundToHundredth(this.consumedDinnerFatQty);
        break;
      case "snacks":
        this.consumedSnacksAlcoholQty = roundToHundredth(this.consumedSnacksAlcoholQty);
        this.consumedSnacksCarbQty = roundToHundredth(this.consumedSnacksCarbQty);
        this.consumedSnacksProteinQty = roundToHundredth(this.consumedSnacksProteinQty);
        this.consumedSnacksFatQty = roundToHundredth(this.consumedSnacksFatQty);
        break;
    }
  }

  // Todo: write sad path unit tests for this method
  // Todo: write happy path unit tests for this method
  private calculateCalories(mealType: string) {
    const caloriesPerGramOfFat = 9;
    const caloriesPerGramOfAlcohol = 7;
    const caloriesPerGramOfProtein = 4;
    const caloriesPerGramOfCarbohydrates = 4;

    switch (mealType) {
      case "breakfast":
        this.consumedBreakfastAlcoholCalories = Math.round(this.consumedBreakfastAlcoholQty * caloriesPerGramOfAlcohol);
        this.consumedBreakfastCarbCalories = Math.round(this.consumedBreakfastCarbQty * caloriesPerGramOfCarbohydrates);
        this.consumedBreakfastProteinCalories = Math.round(this.consumedBreakfastProteinQty * caloriesPerGramOfProtein);
        this.consumedBreakfastFatCalories = Math.round(this.consumedBreakfastFatQty * caloriesPerGramOfFat);
        break;
      case "lunch":
        this.consumedLunchAlcoholCalories = Math.round(this.consumedLunchAlcoholQty * caloriesPerGramOfAlcohol);
        this.consumedLunchCarbCalories = Math.round(this.consumedLunchCarbQty * caloriesPerGramOfCarbohydrates);
        this.consumedLunchProteinCalories = Math.round(this.consumedLunchProteinQty * caloriesPerGramOfProtein);
        this.consumedLunchFatCalories = Math.round(this.consumedLunchFatQty * caloriesPerGramOfFat);
        break;
      case "dinner":
        this.consumedDinnerAlcoholCalories = Math.round(this.consumedDinnerAlcoholQty * caloriesPerGramOfAlcohol);
        this.consumedDinnerCarbCalories = Math.round(this.consumedDinnerCarbQty * caloriesPerGramOfCarbohydrates);
        this.consumedDinnerProteinCalories = Math.round(this.consumedDinnerProteinQty * caloriesPerGramOfProtein);
        this.consumedDinnerFatCalories = Math.round(this.consumedDinnerFatQty * caloriesPerGramOfFat);
        break;
      case "snacks":
        this.consumedSnacksAlcoholCalories = Math.round(this.consumedSnacksAlcoholQty * caloriesPerGramOfAlcohol);
        this.consumedSnacksCarbCalories = Math.round(this.consumedSnacksCarbQty * caloriesPerGramOfCarbohydrates);
        this.consumedSnacksProteinCalories = Math.round(this.consumedSnacksProteinQty * caloriesPerGramOfProtein);
        this.consumedSnacksFatCalories = Math.round(this.consumedSnacksFatQty * caloriesPerGramOfFat);
        break;
    }

    this.consumedAlcoholCalories = this.consumedBreakfastAlcoholCalories + this.consumedLunchAlcoholCalories + this.consumedDinnerAlcoholCalories + this.consumedSnacksAlcoholCalories;

    this.consumedCarbCalories = this.consumedBreakfastCarbCalories + this.consumedLunchCarbCalories + this.consumedDinnerCarbCalories + this.consumedSnacksCarbCalories;

    this.consumedProteinCalories = this.consumedBreakfastProteinCalories + this.consumedLunchProteinCalories + this.consumedDinnerProteinCalories + this.consumedSnacksProteinCalories;

    this.consumedFatCalories = this.consumedBreakfastFatCalories + this.consumedLunchFatCalories + this.consumedDinnerFatCalories + this.consumedSnacksFatCalories;

    this.totalConsumedCalories = this.consumedAlcoholCalories + this.consumedCarbCalories + this.consumedProteinCalories + this.consumedFatCalories;
  }
}
